import type { DeepNonNullable, ValuesType } from "utility-types"

import i18n from "@/translations/i18n"

import type { SensorState } from "./sensor-events"

import { IMAGE_HOST_URL } from "../constants/imgix"
import { makeValidator } from "./type-guards"

export type InstalledOrientation = `bow` | `port` | `starboard` | `stern`
export const SensorPorts = {
  ONE: 1,
  TWO: 2,
} as const
type SensorPort = ValuesType<typeof SensorPorts>

export type GpsType = "CAM_M8Q" | "SAM_M8Q"
export const HardwareGenerations = {
  FC2: "FC2",
  PC1: "PC1",
  TC3: "TC3",
} as const
export const isValidHardwareGeneration = makeValidator(
  Object.values(HardwareGenerations),
)
export type HardwareGeneration = ValuesType<typeof HardwareGenerations>
export const SprinklerTypes = {
  BOOM: "boom",
  GUN: "gun",
} as const
export const SensorPortModes = {
  CURRENT_LOOP: "LOOP",
  SCALED_VOLTAGE: "SCALE",
} as const
export const MeasurementTypes = {
  PULSE: "PULSE",
  ANALOG: "ANALOG",
} as const
export type SprinklerType = ValuesType<typeof SprinklerTypes>
export const ApplicationTypes = {
  MANURE: "manure",
  OTHER: "other",
  WATER: "water",
} as const
export type ApplicationType = ValuesType<typeof ApplicationTypes>

export type ParticlePlatform = "argon" | "boron" | "bsom" | "tracker"
export const SwitchTypes = {
  CLOSED: "C",
  OPEN: "O",
} as const
export type SwitchType = ValuesType<typeof SwitchTypes>

export const InstallationTypes = {
  CENTER_PIVOT: "center_pivot",
  CENTER_PIVOT_CONTROLLER: "center_pivot_controller",
  CENTER_PIVOT_MONITOR: "center_pivot_monitor",
  LINEAR_MOVE: "linear_move",
  LINEAR_MOVE_CONTROLLER: "linear_move_controller",
  LINEAR_MOVE_MONITOR: "linear_move_monitor",
  PROTOTYPE: "prototype",
  PUMP: "pump",
  PUMP_OFF_ONLY: "pump_off_only",
  PUMP_ON_OFF: "pump_on_off",
  PUMP_VFD: "pump_vfd",
  REEL: "reel",
  REEL_WITH_BOOSTER: "reel_with_booster_off_only",
  TRAVELLER_SOFT: "traveller_soft",
  UNCONFIGURED: "unconfigured",
  VALVE: "valve",
} as const

export type InstallationTypeName = ValuesType<typeof InstallationTypes>

export type InstallationType<
  K extends InstallationTypeName = InstallationTypeName,
> = K extends K ? K : never

/**
 * Does it move?
 */
export function isTravelerInstallationType(
  deviceInstallationType: InstallationType | null | undefined,
) {
  switch (deviceInstallationType) {
    case "center_pivot":
    case "center_pivot_controller":
    case "center_pivot_monitor":
    case "linear_move":
    case "linear_move_controller":
    case "linear_move_monitor":
    case "traveller_soft": {
      return true
    }
    default: {
      return false
    }
  }
}

/**
 * Is this a center pivot installation type?
 */
export function isCenterPivotInstallationType(
  deviceInstallationType: InstallationType | null | undefined,
): boolean {
  return (
    deviceInstallationType === "center_pivot" ||
    deviceInstallationType === "center_pivot_controller" ||
    deviceInstallationType === "center_pivot_monitor"
  )
}
export function isReelInstallationType(
  deviceInstallationType: InstallationType | null | undefined,
) {
  switch (deviceInstallationType) {
    case "reel_with_booster_off_only":
    case "reel": {
      return true
    }
    default: {
      return false
    }
  }
}

export function formatInstallationType(
  deviceInstallationType: InstallationType,
) {
  return i18n.t(`deviceInstallationTypes.${deviceInstallationType}`)
}

/**
 *
 */
export function isDeviceInMotion(sensorState: SensorState | null | undefined) {
  if (
    sensorState === "WF" ||
    sensorState === "WL" ||
    sensorState === "RE" ||
    sensorState === "RR"
  ) {
    return true
  }
  return false
}
export type ParticleProductId = number

export const PRESSURE_SENSOR_MODEL_IDS = [
  "SOWAY500",
  "MEAS",
  "SOWAY",
  "TRAFAG",
  "TC3_DEFAULT",
] as const

export type PressureSensorModelId = (typeof PRESSURE_SENSOR_MODEL_IDS)[number]

/**
 * The names of sensors that can be configured by users
 */
export const MUTABLE_SENSOR_NAMES = [
  "depth",
  "flow",
  "humidity",
  "pressure",
  "pressureSwitch",
  "reel",
  "temperature",
  "wheel",
] as const

export type MutableSensorName = (typeof MUTABLE_SENSOR_NAMES)[number]

/**
 * Other sensors are configured by the system
 */
export const CONFIG_SENSOR_NAMES = [
  ...MUTABLE_SENSOR_NAMES,
  "battery",
  "gps",
  "hallSwitch",
  "humidity",
  "icm",
  "relay",
  "vfd",
] as const
export interface SensorConfigurations {
  battery: {
    ioPin: number | null | undefined
    voltageIntercept: number | null | undefined
    voltageSlope: number | null | undefined
    voltageThresholdLowMv: number | null | undefined
  }
  depth: {
    i2cAddress: number | null | undefined
    sensorPort: number | null | undefined
    linearSlopeM: number | null | undefined
    linearInterceptM: number | null | undefined
    normalThresholdM: number | null | undefined
    highThresholdM: number | null | undefined
    overThresholdM: number | null | undefined
    thresholdBufferM: number | null | undefined
  }
  flow: {
    mlPerPulse: number | null | undefined
    sensorPort: SensorPort | null | undefined
    sensorPortMode: "SCALE" | "LOOP" | null | undefined
    measurementType: "PULSE" | "ANALOG" | null | undefined
    normalThresholdLpm: number | null | undefined
    overThresholdLpm: number | null | undefined
    slope: number | null | undefined
    intercept: number | null | undefined
  }
  gps: {
    gpsType: GpsType | null | undefined
  }
  hallSwitch: {
    sensorPort: number | null | undefined
    switchType: SwitchType | null | undefined
    threshold: number | null | undefined
  }
  humidity: {
    normalThreshold: number | null | undefined
    overThreshold: number | null | undefined
  }
  icm: {
    installedOrientation: InstalledOrientation | null | undefined
  }
  pressure: {
    calibrationIntercept: number | null | undefined
    calibrationSlope: number | null | undefined
    sensorModelId: PressureSensorModelId | null | undefined
    sensorPort: SensorPort | null | undefined
    thresholdPsiLower: number | null | undefined
    thresholdPsiUpper: number | null | undefined
  }
  pressureSwitch: {
    sensorPort: SensorPort | null | undefined
    switchType: SwitchType | null | undefined
    threshold: number | null | undefined
  }
  reel: {
    applicationType: ApplicationType | null | undefined
    flowRateOverrideLpm: number | null | undefined
    hoseDiameterMm: number | null | undefined
    hoseWheelMmPerMagnet: number | null | undefined
    linearSpeedMmHMax: number | null | undefined
    linearSpeedMmHMin: number | null | undefined
    nMagnets: number | null | undefined
    nNozzles: number | null | undefined
    nWrapsOuterLayer: number | null | undefined
    nozzleDiameterMm: number | null | undefined
    outerHoseWrapRadiusMm: number | null | undefined
    sensorPort: SensorPort | null | undefined
    speedMeasurementMethod: "spool_rotation" | "hose_wheel" | null | undefined
    sprinklerType: SprinklerType | null | undefined
    swathWidthMm: number | null | undefined
    widthMm: number | null | undefined
  }
  relay: {
    ioPin: number | null | undefined
  }
  temperature: {
    normalThreshold: number | null | undefined
    overThreshold: number | null | undefined
  }
  vfd: {
    i2cAddress: number | null | undefined
    ioPin: number | null | undefined
    maxTrackingPercentage: number | null | undefined
    minTrackingPercentage: number | null | undefined
  }
  wheel: {
    diameterMm: number | null | undefined
    milliRpmFast: number | null | undefined
    milliRpmSlow: number | null | undefined
    nMagnets: number | null | undefined
    sensorPort: SensorPort | null | undefined
    stoppedTimeoutMs: number | null | undefined
  }
}
export type ConfigSensorName = keyof SensorConfigurations

export type SensorConfigKey<S extends ConfigSensorName = ConfigSensorName> =
  S extends S ? keyof SensorConfigurations[S] : never

export type SensorConfig<S extends ConfigSensorName = ConfigSensorName> =
  Partial<SensorConfigurations[S]>

export const SENSOR_NAMES = [...CONFIG_SENSOR_NAMES, "device", "ioExpander", "pivotControl"] as const

// export type SensorName<
//   T extends (typeof SENSOR_NAMES)[number] = (typeof SENSOR_NAMES)[number],
// > = T extends T ? T : never
export type SensorName = (typeof SENSOR_NAMES)[number]
export type GetSensorName<S extends SensorName> = S extends S ? S : never
/**
 * Get the names of all non-null sensors in device configuration
 *
 * @param dc
 */
export function listConfigSensors(dc: {
  [key in SensorName]?: unknown
}): ConfigSensorName[] {
  return CONFIG_SENSOR_NAMES.filter((sn): sn is ConfigSensorName => {
    return Boolean(dc[sn])
  })
}
// type T = SensorConfigKey
export const SENSOR_DEFAULTS: DeepNonNullable<SensorConfigurations> = {
  battery: {
    ioPin: 2,
    voltageIntercept: 0,
    voltageSlope: 107,
    voltageThresholdLowMv: 12000,
  },
  depth: {
    i2cAddress: 96,
    sensorPort: 1,
    linearSlopeM: 1,
    linearInterceptM: 0,
    normalThresholdM: 5,
    highThresholdM: 7,
    overThresholdM: 8,
    thresholdBufferM: 0.3,
  },
  flow: {
    mlPerPulse: 10000,
    sensorPort: 1,
    sensorPortMode: "SCALE",
    measurementType: "PULSE",
    normalThresholdLpm: 1,
    overThresholdLpm: 100000,
    slope: 1,
    intercept: 0
  },
  gps: {
    gpsType: "SAM_M8Q",
  },
  hallSwitch: {
    sensorPort: 1,
    switchType: "O",
    threshold: 2000,
  },
  humidity: {
    normalThreshold: 40,
    overThreshold: 90,
  },
  icm: {
    installedOrientation: "bow",
  },
  pressure: {
    calibrationIntercept: 386.5,
    calibrationSlope: 12.753,
    sensorModelId: "TC3_DEFAULT",
    sensorPort: 2,
    thresholdPsiLower: 60,
    thresholdPsiUpper: 140,
  },
  pressureSwitch: {
    sensorPort: 1,
    switchType: "O",
    threshold: 2000,
  },
  reel: {
    applicationType: "water",
    flowRateOverrideLpm: 0,
    hoseDiameterMm: 127,
    hoseWheelMmPerMagnet: 0,
    linearSpeedMmHMax: 402396,
    linearSpeedMmHMin: 12192,
    nMagnets: 16,
    nNozzles: 1,
    nWrapsOuterLayer: 7,
    nozzleDiameterMm: 25,
    outerHoseWrapRadiusMm: 1651,
    sensorPort: 1,
    speedMeasurementMethod: "spool_rotation",
    sprinklerType: "gun",
    swathWidthMm: 76200,
    widthMm: 1524,
  },
  relay: {
    ioPin: 200,
  },
  temperature: {
    normalThreshold: 0,
    overThreshold: 30,
  },
  vfd: {
    i2cAddress: 96,
    ioPin: 0,
    maxTrackingPercentage: 100,
    minTrackingPercentage: 0,
  },
  wheel: {
    diameterMm: 508,
    milliRpmFast: 1663,
    milliRpmSlow: 49,
    nMagnets: 2,
    sensorPort: 1,
    // Matches backend constants.WHEEL_SENSOR_DEFAULT_STOPPED_TIMEOUT_MS
    stoppedTimeoutMs: 1200000,
  },
}

export function formatPressureSensorModel(name: PressureSensorModelId) {
  switch (name) {
    case "SOWAY500": {
      return i18n.t("deviceConfiguration:pressureSensorModelNameSoway500")
    }
    case "MEAS": {
      return i18n.t("deviceConfiguration:pressureSensorModelNameMeas")
    }
    case "SOWAY": {
      return i18n.t("deviceConfiguration:pressureSensorModelNameSoway")
    }
    case "TRAFAG": {
      return i18n.t("deviceConfiguration:pressureSensorModelNameTrafag")
    }
    case "TC3_DEFAULT":
    default: {
      return i18n.t("deviceConfiguration:pressureSensorModelNameOther")
    }
  }
}
export function getPressureSensorModelImageUri(
  modelName: PressureSensorModelId,
) {
  let fileName: string
  switch (modelName) {
    case "SOWAY500": {
      fileName = "SOWAY500"
      break
    }
    case "MEAS": {
      fileName = "meas"
      break
    }
    case "SOWAY": {
      fileName = "soway"
      break
    }
    case "TRAFAG": {
      fileName = "trafag"
      break
    }
    case "TC3_DEFAULT": {
      fileName = "tc3_default"
      break
    }
  }
  return `${IMAGE_HOST_URL}/support/pressure-sensors/${fileName}.png`
}
